import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '@/store/authStore';

export type LeadsAssignScope = 'all' | 'mine' | 'unassigned';
export type LeadsViewMode = 'kanban' | 'table';

export function useLeadsBoardFilters() {
  const { user } = useAuthStore();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assignScope, setAssignScope] = useState<LeadsAssignScope>('all');
  const [teamFilter, setTeamFilter] = useState('');
  const [missingTeamOnly, setMissingTeamOnly] = useState(false);
  const [viewMode, setViewMode] = useState<LeadsViewMode>(() => {
    try {
      const stored = localStorage.getItem('leads-view-mode');
      return stored === 'table' ? 'table' : 'kanban';
    } catch {
      return 'kanban';
    }
  });

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    try {
      localStorage.setItem('leads-view-mode', viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  const apiFilters = useMemo(() => {
    const f: {
      priority?: string;
      assigned_to?: string;
      sales_team_id?: string;
      missing_team?: boolean;
      search?: string;
    } = {};
    if (priorityFilter) f.priority = priorityFilter;
    if (assignScope === 'mine' && user?.id) f.assigned_to = user.id;
    if (missingTeamOnly) f.missing_team = true;
    else if (teamFilter) f.sales_team_id = teamFilter;
    if (debouncedSearch) f.search = debouncedSearch;
    return f;
  }, [priorityFilter, assignScope, user?.id, teamFilter, missingTeamOnly, debouncedSearch]);

  const hasActiveFilters =
    !!priorityFilter ||
    assignScope !== 'all' ||
    !!teamFilter ||
    missingTeamOnly ||
    !!debouncedSearch;

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setPriorityFilter('');
    setAssignScope('all');
    setTeamFilter('');
    setMissingTeamOnly(false);
  };

  return {
    searchInput,
    setSearchInput,
    debouncedSearch,
    priorityFilter,
    setPriorityFilter,
    assignScope,
    setAssignScope,
    teamFilter,
    setTeamFilter,
    missingTeamOnly,
    setMissingTeamOnly,
    viewMode,
    setViewMode,
    apiFilters,
    hasActiveFilters,
    clearFilters,
  };
}
